import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { formatPhone } from '../../shared/phone';
import { Input } from './Field';
import { Listbox, ListboxOption } from './Listbox';

/**
 * Phone number with its country code: a country picker beside the number.
 *
 * The value is one string the backend can read: `+<code> <number as typed>`
 * (`'+1 (301) 717-0709'`), or `''` while there's no number. The backend
 * stores it as E.164. A stored E.164 value (`'+13017170709'`) opens on its
 * country; a value without a `+` is read as North American.
 */
export interface Country {
  iso: string;
  name: string;
  dial: string;
}

export const COUNTRIES: Country[] = [
  { iso: 'US', name: 'United States', dial: '1' },
  { iso: 'CA', name: 'Canada', dial: '1' },
  { iso: 'GB', name: 'United Kingdom', dial: '44' },
  { iso: 'IE', name: 'Ireland', dial: '353' },
  { iso: 'AU', name: 'Australia', dial: '61' },
  { iso: 'NZ', name: 'New Zealand', dial: '64' },
  { iso: 'MX', name: 'Mexico', dial: '52' },
  { iso: 'BR', name: 'Brazil', dial: '55' },
  { iso: 'FR', name: 'France', dial: '33' },
  { iso: 'DE', name: 'Germany', dial: '49' },
  { iso: 'ES', name: 'Spain', dial: '34' },
  { iso: 'IT', name: 'Italy', dial: '39' },
  { iso: 'NL', name: 'Netherlands', dial: '31' },
  { iso: 'AE', name: 'United Arab Emirates', dial: '971' },
  { iso: 'IN', name: 'India', dial: '91' },
  { iso: 'SG', name: 'Singapore', dial: '65' },
  { iso: 'HK', name: 'Hong Kong', dial: '852' },
  { iso: 'JP', name: 'Japan', dial: '81' },
  { iso: 'ZA', name: 'South Africa', dial: '27' },
];

const OPTIONS: ListboxOption[] = COUNTRIES.map((c) => ({
  value: c.iso,
  label: c.name,
  description: `+${c.dial}`,
  short: `+${c.dial}`,
}));

// Longest first, so +353 isn't read as +3 followed by 53.
const DIALS_LONGEST_FIRST = COUNTRIES.map((c) => c.dial)
  .filter((dial, i, all) => all.indexOf(dial) === i)
  .sort((a, b) => b.length - a.length);

const byIso = (iso: string) =>
  COUNTRIES.find((c) => c.iso === iso) ?? COUNTRIES[0];

/** Split a stored or emitted value into its country and the number part. */
export const splitPhone = (
  value: string,
  preferIso = 'US',
): { iso: string; national: string } => {
  // Only leading space goes: the number keeps a space typed at its end.
  const raw = value.replace(/^\s+/, '');
  if (!raw.startsWith('+')) return { iso: preferIso, national: raw };
  // Our own value: "+44 20 7946 0958".
  const spaced = raw.match(/^\+(\d{1,4})\s+(.*)$/);
  const digits = raw.replace(/\D/g, '');
  const dial = spaced
    ? spaced[1]
    : DIALS_LONGEST_FIRST.find((d) => digits.startsWith(d));
  if (!dial) return { iso: preferIso, national: raw };
  const country =
    COUNTRIES.find((c) => c.dial === dial && c.iso === preferIso) ??
    COUNTRIES.find((c) => c.dial === dial);
  if (!country) return { iso: preferIso, national: raw };
  const national = spaced ? spaced[2] : digits.slice(dial.length);
  return {
    iso: country.iso,
    national:
      dial === '1' && !spaced ? formatPhone(national) || national : national,
  };
};

export const joinPhone = (iso: string, national: string) => {
  if (!national.trim()) return '';
  const { dial } = byIso(iso);
  let number = national.trimStart();
  // "1 301 717 0709" with +1 picked: the 1 is the country code again.
  if (dial === '1' && /^1\d{10}$/.test(number.replace(/\D/g, '')))
    number = number.replace(/^\D*1\D*/, '');
  return `+${dial} ${number}`;
};

export interface PhoneInputProps {
  /** Goes on the number, so a `Field` label names it. */
  id?: string;
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  'aria-describedby'?: string;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  id,
  value,
  onChange,
  invalid,
  disabled,
  placeholder = '(555) 123-4567',
  className,
  'aria-describedby': describedBy,
}) => {
  const [iso, setIso] = useState(() => splitPhone(value).iso);
  const { national } = splitPhone(value, iso);
  // A new stored value (loaded, or reset) brings its own country.
  useEffect(() => {
    if (value.trim().startsWith('+')) setIso((at) => splitPhone(value, at).iso);
  }, [value]);

  return (
    <div className={clsx('flex gap-2', className)}>
      <Listbox
        aria-label="Country code"
        className="w-[84px] shrink-0"
        value={iso}
        options={OPTIONS}
        disabled={disabled}
        invalid={invalid}
        onChange={(next) => {
          setIso(next);
          onChange(joinPhone(next, national));
        }}
      />
      <Input
        id={id}
        aria-describedby={describedBy}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        className="v2-num min-w-0 flex-1"
        placeholder={placeholder}
        value={national}
        invalid={invalid}
        disabled={disabled}
        onChange={(e) => {
          const typed = e.target.value;
          // A pasted "+44 20 7946 0958" carries its own country.
          onChange(
            typed.trim().startsWith('+') ? typed : joinPhone(iso, typed),
          );
        }}
      />
    </div>
  );
};
